import hash from 'object-hash';
import { Status } from '@/main/store/@types/page';
import { objectToCamelCaseShallow, toDbFieldName, formatDecimalValue } from '@/main/utils/stringHelper';
import _ from 'lodash';

import { formatDate, formatMonthYear, formatMonthYearandtime } from './dateFormatter';

export { EditableStatus } from '@/main/components/constants';

export type ValueType = null | 'Boolean' | 'String' | 'Int32' | 'Int64' | 'Decimal' | 'Byte' | 'Date' | 'Time' | 'DateTime';

export const COLUMN_ID_INDEX = '0';

export type ColumnsSetting = { isEditable: boolean, required?: boolean, type?: string,
	align?: string, minWidth?: string | number, maxlength?: string | number };

export interface Column {
	columnId: string;
	columnName: string;
	columns?: Column[];
	isVisible: boolean;
	propertyName: string;
	valueType: ValueType;
	isEditable?: boolean;
	required?: boolean;
	iconClass?: string;
}

export interface Row {
	cells: Cell[];
	operations: number;
}

export type Cell = any;

export interface CellNamesObject extends Record<string, any> {
	idColumn: number;
}

export enum ObjectType { columnName, propertyName }

/**
 * Table in List {@link @/main/pages/list#default} components,
 * part of the state of Edit components with dependent tables.
 */
export class Table {
	public columns: Column[] = [];

	prepareDefaultRowModel() {
		const cells = _.reduce(this.columns, (result, { columnId }) => ({ ...result, [columnId]: null }), {});
		this.defaultRowModel = { cells };
	}

	public rows: Row[] = [];
	public name: string = '';
	public gridId: number | null = null;
	public emptyTextKey: string = 'TableMsgNoData';
	public hasIdColumn: boolean = true;
	public sort = null as { columnId: any; order: string } | null;
	public loadingCount: number = 0;
	public operations: number = 0;
	public status: Status = Status.ok;
	public cellNames: (Record<string, number> & { idColumn: number }) | null = null;
	public hash: string | null = null;
	public isEditable = false;
	public loadByEntityId = false;
	public columnSettings = null as Record<string, ColumnsSetting> | null;
	public defaultRowModel = null as Record<string, any> | null;
	public computeHashOnLoad = false;

	public constructor(init?: Partial<Table>) {
		Object.assign(this, init);
	}

	public addRowId() {
		let rowIdSekvence = 1;

		this.columns.push({
			columnId: 'rowId',
			columnName: 'RowId',
			isVisible: false,
			propertyName: 'rowId',
			valueType: <ValueType>'Number',
		});
		this.rows.forEach((row) => {
			// eslint-disable-next-line no-plusplus
			(row.cells as any).rowId = rowIdSekvence++;
		});
	}

	public addUpdateStatusColumn() {
		const alreadyExists = this.columns.some((col) => col.columnName === 'UpdateStatus');
		if (alreadyExists) return;

		this.columns.push({
			columnId: 'UpdateStatus',
			columnName: 'UpdateStatus',
			isVisible: false,
			propertyName: 'updateStatus',
			valueType: <ValueType>'Number',
		});
	}

	public isModified() {
		return this.hash !== hash(this.rows);
	}
}

export function cellNames(table: { columns: Column[] }): CellNamesObject {
	return getFlatColumns(table.columns).reduce((obj: any, curr: Column) => {
		obj[curr.columnName] = curr.columnId;

		return obj;
	}, { idColumn: table.columns.length ? Number(table.columns[0].columnId) : 0 });
}

export function getColumnsRowMapObject<T>(columns: Column[], row: Record<number, T>, objectType = ObjectType.columnName, forExport: boolean = false) {
	let data = {};
	columns.forEach((column) => {
		const name = (objectType === ObjectType.columnName) ? column.columnName : column.propertyName;
		let value : any = row[Number(column.columnId)];
		if (column.valueType === 'DateTime' || column.valueType === 'Date') {
			value = formatMonthYearandtime(value);
		} else if (column.valueType === 'Decimal' && value && value !== 0) {
			value = formatDecimalValue(value);
		} else if (forExport && column.valueType === 'String' && value) {
			value = `${value}\t`;
		}
		(<any>data)[name] = value;
		if (column.columns) {

			data = { ...data, ...getColumnsRowMapObject(column.columns, row, objectType, forExport) };
		}
	});
	if (columns && columns.length > 0 && Number(columns[0].columnId) === 0) {
		data = { ID: row[Number(columns[0].columnId)], ...data };
	}
	return data as { ID: any } & T;
}
export function getColumnsRowMapObjectForExportFunc<T>(columns: Column[], row: Record<number, T>, objectType = ObjectType.columnName, forExport: boolean = false) {
	let data = {};
	columns.forEach((column) => {
		const name = (objectType === ObjectType.columnName) ? column.columnName : column.propertyName;
		let value : any = row[Number(column.columnId)];
		if (column.valueType === 'DateTime' || column.valueType === 'Date') {
			value = formatMonthYearandtime(value);
			if (!_.isNull(value)) value = `${value}\t`;
		} else if (column.valueType === 'Decimal' && value && value !== 0) {
			value = formatDecimalValue(value);
		} else if (forExport && column.valueType === 'String' && value) {
			value = `${value}\t`;
		}
		(<any>data)[name] = value;
		if (column.columns) {
			data = { ...data, ...getColumnsRowMapObject(column.columns.filter(x => x.isVisible === true), row, objectType, forExport) };
		}
	});
	return data;
}
export function getFlatColumns(columns: Column[]): Column[] {
	return columns.reduce((accumulator: Column[], column: Column) => {
		if (column.columns && column.columns.length > 0) {
			return [...accumulator, ...getFlatColumns(column.columns)];
		}
		return [...accumulator, column];
	}, [] as Column[]);
}
// TODO: zjednodusit pouzit map pro citelnost
export function arrayToTable(data: any, loadingCount = 0, operations = 0) {
	const tableRows = [] as Row[];
	const tableSchema = [] as Column[];
	data.forEach((row: {}) => {
		const cells = Object.values(row);
		const colNames = Object.keys(row);
		if (tableSchema.length === 0) {
			colNames.forEach((col, index) => {
				const column = {
					columnId: index.toString(),
					columnName: toDbFieldName(col),
					isVisible: false,
					propertyName: col,
					valueType: index === 0 ? 'Int32' : 'String',
				} as Column;
				tableSchema.push(column);
			});
		}
		tableRows.push({ cells, operations } as Row);
	});
	return {
		loadingCount,
		rows: tableRows,
		columns: tableSchema,
	} as Table;
}

export function convertToObjects({ rows, columns }: { rows: Row[], columns: Column[] }) {
	return rows.map(
		(row) => columns!.reduce(
			(adapter: any, column) => {
				if (column.propertyName) {
					adapter[column.propertyName] = row.cells[column.columnId as any];
				}

				return adapter;
			},
			{},
		),
	);
}

export function rowCloseEvent(rowKey: string | number) { return `row-close-${rowKey}`; }

export function columnsToCamelCase(columns: any[]): Column[] {
	return columns.map((c) => {
		const col = objectToCamelCaseShallow(c);

		'columns' in col && (col.columns = columnsToCamelCase(col.columns));

		return col;
	});
}
export function getColumnsNames(columns: Column[]) {
	let data = [] as any;
	columns.forEach((column) => {
		const name = column.columnName;
		data.push(name);
		if (column.columns) {
			data = [...data, ...getColumnsNames(column.columns)];
		}
	});
	return data;
}


export function intToRgb(rgbInt: number) {
	/* eslint-disable no-bitwise */
	const FULL_RGB = 0xff_ff_ff;
	if (!Number.isInteger(rgbInt) || rgbInt < 0 || rgbInt > FULL_RGB) { return null; }
	const r = (rgbInt >> 16) & 255;
	const g = (rgbInt >> 8) & 255;
	const b = rgbInt & 255;
	return `rgb(${r}, ${g}, ${b})`;
}
